import { jsPDF as JsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import currentTimeZone from '../../../enum/timeZone';
import i18n from '../../../i18n';
import { InvoiceInterface } from '../../../interface/invoice-interface';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getAmiriFontBase64 } from '../../../shared-functions/ExportFile';
import FormatTimeDuration from '../../../shared-functions/FormatTimeDuration';

/**
 * Generate, download, and open a PDF document for a single invoice with locale support.
 * @param {InvoiceInterface} invoice - The invoice data object.
 * @param {string} [locale] - Optional locale code (e.g. 'en', 'ar', 'fr').
 * @returns {Promise<boolean>} Resolves to true when the PDF generation is complete, false on failure.
 */
const downloadSingleInvoicePdf = async (
  invoice: InvoiceInterface,
  locale?: string,
): Promise<boolean> => {
  try {
    const activeLocale = locale || i18n.language || 'en';
    const isArabic = activeLocale.startsWith('ar');

    const document = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    let activeFont = 'helvetica';

    if (isArabic) {
      const fontBase64 = await getAmiriFontBase64();
      if (fontBase64) {
        document.addFileToVFS('Amiri-Regular.ttf', fontBase64);
        document.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        document.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
        document.setFont('Amiri');
        activeFont = 'Amiri';
      }
    }

    /**
     * Processes text for Arabic letter shaping and bi-directional reordering.
     * @param {string} text - The input text.
     * @returns {string} Processed text.
     */
    const formatText = (text: string): string => {
      if (isArabic && typeof (document as any).processArabic === 'function') {
        return (document as any).processArabic(text);
      }
      return text;
    };

    /**
     * Translates a given key using the invoice namespace and active locale.
     * @param {string} key - The translation key.
     * @param {string} defaultValue - The fallback string.
     * @returns {string} The translated string.
     */
    const translate = (key: string, defaultValue: string): string => {
      const translated = i18n.t(key, { lng: activeLocale, ns: 'invoice', defaultValue });
      return formatText(translated);
    };

    /**
     * Formats a UTC date string according to the active locale and current time zone.
     * @param {string} [utcDateString] - The UTC date string to format.
     * @returns {string} Formatted localized date time string.
     */
    const formatDateTime = (utcDateString?: string): string => {
      if (!utcDateString) {
        return '-';
      }
      const parsedDate = new Date(utcDateString);
      if (Number.isNaN(parsedDate.getTime())) {
        return '-';
      }
      try {
        const formatted = new Intl.DateTimeFormat(activeLocale, {
          timeZone: currentTimeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }).format(parsedDate);
        return formatText(formatted);
      } catch {
        return formatText(ConvertUTCtoDateTime(utcDateString));
      }
    };

    /**
     * Formats a numeric value according to the active locale.
     * @param {number | string | undefined | null} amountValue - The value to format.
     * @param {number} [fractionDigits] - The number of fraction digits.
     * @returns {string} Formatted number string.
     */
    const formatNumber = (
      amountValue: number | string | undefined | null,
      fractionDigits: number = 2,
    ): string => {
      const numericValue = Number(amountValue || 0);
      try {
        const formatted = new Intl.NumberFormat(activeLocale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }).format(numericValue);
        return formatText(formatted);
      } catch {
        return formatText(numericValue.toFixed(fractionDigits));
      }
    };

    const currency = invoice.currency || 'INR';
    const details = invoice.chargingDetails;
    const breakdown = details?.costBreakdown;

    // Header Title
    document.setFont(activeFont, 'bold');
    document.setFontSize(20);
    document.setTextColor(40, 40, 40);
    if (isArabic) {
      document.text(translate('taxInvoice', 'TAX INVOICE'), 196, 20, { align: 'right' });
    } else {
      document.text(translate('taxInvoice', 'TAX INVOICE'), 14, 20);
    }

    // Invoice Meta
    document.setFont(activeFont, 'normal');
    document.setFontSize(10);
    document.setTextColor(100, 100, 100);

    const invoiceNumberText = `${translate('invoiceNumber', 'Invoice No')}: ${invoice.invoiceNumber || '-'}`;
    const invoiceDateText = `${translate('invoiceDate', 'Invoice Date')}: ${formatDateTime(invoice.invoiceDate)}`;
    const financialYearText = `${translate('financialYear', 'Financial Year')}: ${invoice.financialYear || '-'}`;

    if (isArabic) {
      document.text(invoiceNumberText, 196, 28, { align: 'right' });
      document.text(invoiceDateText, 196, 34, { align: 'right' });
      document.text(financialYearText, 196, 40, { align: 'right' });
    } else {
      document.text(invoiceNumberText, 14, 28);
      document.text(invoiceDateText, 14, 34);
      document.text(financialYearText, 14, 40);
    }

    // Divider
    document.setDrawColor(220, 220, 220);
    document.line(14, 45, 196, 45);

    // Invoice From & Invoice To Box
    document.setFont(activeFont, 'bold');
    document.setFontSize(11);
    document.setTextColor(40, 40, 40);

    const fromTitle = translate('invoiceFrom', 'Invoice From (Provider)');
    const toTitle = translate('invoiceTo', 'Invoice To (Customer)');

    const fromX = isArabic ? 196 : 14;
    const toX = isArabic ? 100 : 110;
    const textAlign = isArabic ? 'right' : 'left';

    document.text(fromTitle, fromX, 52, { align: textAlign });
    document.text(toTitle, toX, 52, { align: textAlign });

    document.setFont(activeFont, 'normal');
    document.setFontSize(9.5);
    document.setTextColor(80, 80, 80);

    // From
    document.text(
      `${translate('name', 'Name')}: ${formatText(invoice.invoiceFrom?.name || 'Energic')}`,
      fromX,
      58,
      { align: textAlign },
    );
    document.text(
      `${translate('email', 'Email')}: ${invoice.invoiceFrom?.email || '-'}`,
      fromX,
      63,
      { align: textAlign },
    );
    document.text(
      `${translate('gstin', 'GSTIN')}: ${invoice.invoiceFrom?.gstin || '-'}`,
      fromX,
      68,
      { align: textAlign },
    );
    document.text(
      `${translate('address', 'Address')}: ${formatText(invoice.invoiceFrom?.billingAddress || '-')}`,
      fromX,
      73,
      { align: textAlign, maxWidth: 85 },
    );

    // To
    document.text(
      `${translate('name', 'Name')}: ${formatText(invoice.invoiceTo?.name || '-')}`,
      toX,
      58,
      { align: textAlign },
    );
    document.text(
      `${translate('email', 'Email')}: ${invoice.invoiceTo?.email || '-'}`,
      toX,
      63,
      { align: textAlign },
    );
    document.text(
      `${translate('mobile', 'Mobile')}: ${invoice.invoiceTo?.mobile || '-'}`,
      toX,
      68,
      { align: textAlign },
    );
    document.text(
      `${translate('location', 'Location')}: ${formatText(invoice.invoiceTo?.chargingLocation || '-')}`,
      toX,
      73,
      { align: textAlign, maxWidth: 85 },
    );

    // Charger Info Section
    document.line(14, 85, 196, 85);
    document.setFont(activeFont, 'bold');
    document.setFontSize(11);
    document.setTextColor(40, 40, 40);
    document.text(
      translate('sessionDetails', 'Charging & Session Details'),
      fromX,
      92,
      { align: textAlign },
    );

    autoTable(document, {
      startY: 96,
      theme: 'grid',
      styles: {
        font: activeFont,
        halign: isArabic ? 'right' : 'left',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        font: activeFont,
        halign: isArabic ? 'right' : 'left',
      },
      head: [[translate('description', 'Description'), translate('details', 'Details')]],
      body: [
        [translate('sessionId', 'Session ID'), invoice.sessionId || '-'],
        [translate('transactionId', 'Transaction ID'), invoice.transactionId || '-'],
        [translate('userId', 'User Custom ID'), invoice.userCustomId || '-'],
        [
          `${translate('chargerId', 'Charger ID')} / ${translate('stationId', 'Station ID')}`,
          `${invoice.chargerInfo?.chargerId || '-'} (${invoice.chargerInfo?.stationId || '-'})`,
        ],
        [
          `${translate('connector', 'Connector')} / ${translate('rating', 'Rating')}`,
          `${invoice.chargerInfo?.connectorType || '-'} / ${invoice.chargerInfo?.connectorRating || '-'}`,
        ],
        [
          translate('startTime', 'Start Time'),
          formatDateTime(invoice.chargingDetails?.startTime),
        ],
        [
          translate('endTime', 'End Time'),
          formatDateTime(invoice.chargingDetails?.endTime),
        ],
        [
          translate('duration', 'Charging Duration'),
          formatText(FormatTimeDuration(invoice.chargingDetails?.chargingDuration)),
        ],
        [
          translate('energyUsed', 'Energy Consumed'),
          `${formatNumber(invoice.chargingDetails?.energyUsed || 0, 3)} kWh`,
        ],
      ],
    });

    const finalY = ((document as any).lastAutoTable?.finalY ?? 170) + 8;

    // Cost Breakdown Table
    document.setFont(activeFont, 'bold');
    document.setFontSize(11);
    document.setTextColor(40, 40, 40);
    document.text(
      translate('costBreakdown', 'Cost Breakdown'),
      fromX,
      finalY,
      { align: textAlign },
    );

    const consumptionCost = breakdown?.consumptionCost ?? (details?.invoiceAmountPaid || 0);
    const platformFee = breakdown?.platformFee ?? 0;
    const sessionFee = breakdown?.sessionFee ?? 0;
    const totalAmount = details?.invoiceAmountPaid || 0;

    autoTable(document, {
      startY: finalY + 4,
      theme: 'striped',
      styles: {
        font: activeFont,
        halign: isArabic ? 'right' : 'left',
      },
      headStyles: {
        fillColor: [46, 204, 113],
        textColor: 255,
        font: activeFont,
        halign: isArabic ? 'right' : 'left',
      },
      head: [[translate('item', 'Item'), translate('amount', 'Amount')]],
      body: [
        [
          translate('consumptionCost', 'Consumption Cost'),
          `${formatNumber(consumptionCost)} ${currency}`,
        ],
        [
          translate('platformFee', 'Platform Fee'),
          `${formatNumber(platformFee)} ${currency}`,
        ],
        [
          translate('surchargeFee', 'Surcharge Fee'),
          `${formatNumber(sessionFee)} ${currency}`,
        ],
        [
          translate('totalPaid', 'Total Amount Paid'),
          `${formatNumber(totalAmount)} ${currency}`,
        ],
      ],
    });

    const invoiceIdentifier =
      invoice.invoiceNumber?.replace(/\//g, '_') || invoice._id || invoice.id || 'invoice';
    const fileName = `Invoice_${invoiceIdentifier}.pdf`;

    try {
      const blobUrl = document.output('bloburl');
      if (blobUrl) {
        window.open(blobUrl, '_blank');
      }
    } catch (outputError) {
      console.warn('Could not open PDF in new tab:', outputError);
    }

    document.save(fileName);
    return true;
  } catch (error) {
    console.error('Failed to generate invoice PDF:', error);
    return false;
  }
};

export default downloadSingleInvoicePdf;
